import { useState, useEffect, useContext } from 'react';
import { AppContext } from '../../context';
import api from '../../hooks/useApi';
import { formatDate } from '../../utils/format';
import { canAccess } from '../../utils/rbac';

function fmt(n) {
  if (n == null || n === '') return '—';
  return '£' + Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const STATUS_GROUPS = {
  active:     ['Active'],
  upcoming:   ['Draft', 'Issued', 'On Hold'],
  historical: ['Awaiting Payment', 'Complete', 'Cancelled', 'Archived', 'Expired'],
};

const STATUS_COLORS = {
  Active:    'bg-emerald-100 text-emerald-700',
  'Awaiting Payment': 'bg-teal-100 text-teal-700',
  Draft:     'bg-gray-100 text-gray-600',
  Issued:    'bg-blue-100 text-blue-700',
  'On Hold': 'bg-amber-100 text-amber-700',
  Complete:  'bg-navy/10 text-navy',
  Cancelled: 'bg-red-100 text-red-600',
  Archived:  'bg-gray-100 text-gray-500',
  Expired:   'bg-orange-100 text-orange-600',
};

export default function MySowsPage() {
  const { currentUser, navigate } = useContext(AppContext);
  const [sows, setSows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('active');
  const [search, setSearch] = useState('');
  // SOW values are the sow:see-financials grant; the API leaves them out for
  // anybody else (pen test AD26341, AD12/AD16), so neither are the columns.
  const seesMoney = canAccess('sow:see-financials');
  const cols = seesMoney ? 7 : 5;

  useEffect(() => {
    if (!currentUser?.id) return;
    api.get('sows', { owner_id: currentUser.id })
      .then(data => setSows(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [currentUser?.id]);

  const grouped = sows.reduce((acc, s) => {
    const key = Object.entries(STATUS_GROUPS).find(([, v]) => v.includes(s.status))?.[0] || 'historical';
    if (!acc[key]) acc[key] = [];
    acc[key].push(s);
    return acc;
  }, {});

  const visible = (grouped[tab] || []).filter(s =>
    !search || [s.reference, s.detail, s.client_name].some(v => v?.toLowerCase().includes(search.toLowerCase()))
  );

  const tabs = [
    { key: 'active',     label: 'Active',     count: (grouped.active || []).length },
    { key: 'upcoming',   label: 'Upcoming',   count: (grouped.upcoming || []).length },
    { key: 'historical', label: 'Historical', count: (grouped.historical || []).length },
  ];

  const totalRemaining = (grouped.active || []).reduce((s, r) => s + (r.remaining_to_invoice || 0), 0);
  const totalValue     = (grouped.active || []).reduce((s, r) => s + (r.total_excl_vat || 0), 0);

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-base font-extrabold text-navy">My Projects</h2>
          <div className="text-[9px] text-gray-500">SOWs where you are the assigned Project Manager</div>
        </div>
        {seesMoney && tab === 'active' && (grouped.active || []).length > 0 && (
          <div className="flex gap-4 text-right">
            <div>
              <div className="text-[8px] text-gray-400 uppercase tracking-wide">Under Management</div>
              <div className="text-sm font-extrabold text-navy">{fmt(totalValue)}</div>
            </div>
            <div>
              <div className="text-[8px] text-gray-400 uppercase tracking-wide">Remaining</div>
              <div className="text-sm font-extrabold text-amber-600">{fmt(totalRemaining)}</div>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 mb-3">
        <div className="flex gap-1">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-3 py-1 text-[10px] font-semibold rounded flex items-center gap-1.5 ${tab === t.key ? 'bg-navy text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              {t.label}
              {t.count > 0 && (
                <span className={`text-[8px] font-bold px-1 rounded-full ${tab === t.key ? 'bg-white/20' : 'bg-gray-200 text-gray-500'}`}>{t.count}</span>
              )}
            </button>
          ))}
        </div>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search reference, name, client…"
          className="ml-auto border border-gray-200 rounded px-2 py-1 text-[10px] w-52 focus:outline-none focus:border-navy"
        />
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-3 py-2 text-[8px] uppercase font-bold text-gray-500">Reference</th>
              <th className="text-left px-3 py-2 text-[8px] uppercase font-bold text-gray-500">Client</th>
              <th className="text-left px-3 py-2 text-[8px] uppercase font-bold text-gray-500">Detail</th>
              <th className="text-left px-3 py-2 text-[8px] uppercase font-bold text-gray-500">Status</th>
              {seesMoney && <th className="text-right px-3 py-2 text-[8px] uppercase font-bold text-gray-500">Value</th>}
              {seesMoney && <th className="text-right px-3 py-2 text-[8px] uppercase font-bold text-gray-500">Remaining</th>}
              <th className="text-left px-3 py-2 text-[8px] uppercase font-bold text-gray-500">SOW Date</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(6)].map((_, i) => (
                <tr key={i} className="border-b border-gray-100">
                  {[...Array(cols)].map((_, j) => (
                    <td key={j} className="px-3 py-2"><div className="h-3 bg-gray-100 rounded animate-pulse" /></td>
                  ))}
                </tr>
              ))
            ) : visible.length === 0 ? (
              <tr><td colSpan={cols} className="px-3 py-8 text-center text-[10px] text-gray-400">
                {search ? 'No SOWs match your search' : `No ${tab} SOWs assigned to you`}
              </td></tr>
            ) : visible.map(s => (
              <tr key={s.id} className="border-b border-gray-100 hover:bg-gold/5 cursor-pointer"
                onClick={() => navigate('sow-detail', s.id)}>
                <td className="px-3 py-2 text-[10px] font-semibold text-navy">{s.reference}</td>
                <td className="px-3 py-2 text-[9px] text-gray-600">{s.client_name || '—'}</td>
                <td className="px-3 py-2 text-[9px] max-w-xs truncate" title={s.detail}>{s.detail || '—'}</td>
                <td className="px-3 py-2">
                  <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded ${STATUS_COLORS[s.status] || 'bg-gray-100 text-gray-500'}`}>{s.status}</span>
                </td>
                {seesMoney && <td className="px-3 py-2 text-[10px] font-semibold text-right">{fmt(s.total_excl_vat)}</td>}
                {seesMoney && <td className={`px-3 py-2 text-[10px] font-semibold text-right ${(s.remaining_to_invoice || 0) > 0 ? 'text-amber-600' : 'text-gray-400'}`}>{fmt(s.remaining_to_invoice)}</td>}
                <td className="px-3 py-2 text-[9px] text-gray-500">{formatDate(s.sow_date) || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
